import React from 'react';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { AdminNavBarHeader } from '../nav/AdminNavBarHeader';
import { Button } from '../ui/Button';

export interface PageHeaderHostProps {
  /**
   * Page title
   */
  title: string;
  /**
   * Optional icon to display before title
   */
  icon?: React.ReactNode;
  /**
   * Back button configuration
   */
  backButton?: {
    label?: string;
    to: string;
    onClick?: () => void;
  };
  /**
   * Additional action buttons or elements to display on the right
   */
  actions?: React.ReactNode;
  /**
   * Navigation flow elements (for guided walkthroughs)
   */
  flowElements?: {
    previous?: {
      label: string;
      to: string;
      onClick?: () => void;
    };
    next?: {
      label: string;
      to: string;
      onClick?: () => void;
    };
  };
  /**
   * Whether to show the header as sticky
   * @default true
   */
  sticky?: boolean;
  /**
   * Additional className
   */
  className?: string;
}

/**
 * PageHeaderHost - Uses shared AdminNavBarHeader component
 * 
 * Provides page-specific header content while maintaining exact same
 * size and styling as sidebar header for smooth animations.
 * 
 * @example
 * ```tsx
 * <PageHeaderHost
 *   title="Company Search"
 *   icon={<Building2 className="w-6 h-6 text-rollops-accent" />}
 *   backButton={{ to: '/dev/dashboard' }}
 * />
 * ```
 */
export function PageHeaderHost({
  title,
  icon,
  backButton,
  actions,
  flowElements,
  sticky = true,
  className = '',
}: PageHeaderHostProps) {

  const leftContent = (
    <div className="flex items-center gap-3 flex-1 min-w-0">
      {backButton && (
        backButton.onClick ? (
          <Button
            variant="outline"
            size="sm"
            onClick={backButton.onClick}
            className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] flex-shrink-0 inline-flex items-center h-8"
          >
            <ArrowLeft className="w-4 h-4 mr-1.5" />
            <span>{backButton.label || 'Back'}</span>
          </Button>
        ) : (
          <Link to={backButton.to}>
            <Button
              variant="outline"
              size="sm"
              className="border-[#504A4A] text-[#EBFFEE] hover:bg-[#363232] flex-shrink-0 inline-flex items-center h-8"
            >
              <ArrowLeft className="w-4 h-4 mr-1.5" />
              <span>{backButton.label || 'Back'}</span>
            </Button>
          </Link>
        )
      )}
      
      {icon && (
        <div className="flex-shrink-0 text-rollops-accent flex items-center">
          {icon}
        </div>
      )}
      
      <h1 className="text-xl font-bold text-[#EBFFEE] truncate flex items-center">
        {title}
      </h1>
    </div>
  );

  const rightContent = (
    <div className="flex items-center gap-2">
      {flowElements && (
        <div className="flex items-center gap-2 border-r border-[#504A4A] pr-3 mr-2">
          {flowElements.previous && (
            <Button
              variant="ghost"
              size="sm"
              onClick={flowElements.previous.onClick}
              className="text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232]"
              asChild={!flowElements.previous.onClick}
            >
              {flowElements.previous.onClick ? (
                <button type="button" className="flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">{flowElements.previous.label}</span>
                </button>
              ) : (
                <Link to={flowElements.previous.to} className="flex items-center gap-1">
                  <ChevronLeft className="w-4 h-4" />
                  <span className="hidden sm:inline">{flowElements.previous.label}</span>
                </Link>
              )}
            </Button>
          )}
          {flowElements.next && (
            <Button
              variant="ghost"
              size="sm"
              onClick={flowElements.next.onClick}
              className="text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232]"
              asChild={!flowElements.next.onClick}
            >
              {flowElements.next.onClick ? (
                <button type="button" className="flex items-center gap-1">
                  <span className="hidden sm:inline">{flowElements.next.label}</span>
                  <ChevronRight className="w-4 h-4" />
                </button>
              ) : (
                <Link to={flowElements.next.to} className="flex items-center gap-1">
                  <span className="hidden sm:inline">{flowElements.next.label}</span>
                  <ChevronRight className="w-4 h-4" />
                </Link>
              )}
            </Button>
          )}
        </div>
      )}
      
      {actions && (
        <div className="flex items-center gap-2">
          {actions}
        </div>
      )}
    </div>
  );

  const header = (
    <AdminNavBarHeader
      leftContent={leftContent}
      rightContent={rightContent}
      className={className}
    />
  );

  if (sticky) {
    // Position fixed at top:0 to match sidebar header exactly (sidebar is also fixed top-0)
    // Use left offset to account for sidebar width, extend to PageLayout edges using negative margins
    // When skipTopPadding is true, PageLayout has no top padding, so header sits at top:0
    return (
      <header 
        className="fixed top-0 z-40"
        style={{
          left: 'var(--sidebar-width, 64px)',
          right: 0,
          marginLeft: 'calc(-1 * var(--page-padding-left, 24px))',
          marginRight: 'calc(-1 * var(--page-padding-right, 24px))',
        }}
      >
        {header}
      </header>
    );
  }

  // Non-sticky: still extend horizontally but keep vertical spacing
  return (
    <header 
      className="mb-6"
      style={{
        marginLeft: 'calc(-1 * var(--page-padding-left, 24px))',
        marginRight: 'calc(-1 * var(--page-padding-right, 24px))',
      }}
    >
      {header}
    </header>
  );
}
